import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Upload, AlertCircle, ListChecks, Layers, ScrollText, Calculator,
  CalendarDays, Banknote, FileText,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, isAccountingRole } from '@/lib/utils';
import { Navigate } from 'react-router-dom';

import { OverviewTab } from './compensation/OverviewTab';
import { CycleTab } from './compensation/CycleTab';
import { RecordsTab } from './compensation/RecordsTab';
import { ReviewsTab } from './compensation/ReviewsTab';
import { ImportTab } from './compensation/ImportTab';
import { StatementsTab } from './compensation/StatementsTab';
import { PaymentsTab } from './compensation/PaymentsTab';
import { RulesTab } from './compensation/RulesTab';
import { AdjustmentsTab } from './compensation/AdjustmentsTab';

// Compensation Center entrypoint — purely a tab router. Each tab lives in
// /pages/compensation/<Name>Tab.jsx so this file stays under ~70 lines.
export default function CompensationPage() {
  const { user } = useAuth();
  const [statementMonth, setStatementMonth] = useState('');
  const [tab, setTab] = useState('overview');

  // Permission gate: KR Admin / SuperAdmin / Accounting only. Phase 3
  // (agent/agency visibility) is intentionally deferred — see PRD.
  if (user && !isKRRole(user) && !isAccountingRole(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6 p-6" data-testid="compensation-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Compensation Center</h1>
        <p className="text-zinc-500 mt-1">Carrier statement imports, proration calculations, multi-payee payouts, and Paid-record adjustment ledger.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview"><Calculator className="h-4 w-4 mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="cycle" data-testid="tab-cycle"><CalendarDays className="h-4 w-4 mr-1" />Cycle</TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-records"><Layers className="h-4 w-4 mr-1" />Records</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews"><AlertCircle className="h-4 w-4 mr-1" />Needs Review</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><Upload className="h-4 w-4 mr-1" />Import</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-statements"><FileText className="h-4 w-4 mr-1" />Statements</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments"><Banknote className="h-4 w-4 mr-1" />Payments</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules"><ListChecks className="h-4 w-4 mr-1" />Rules</TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="tab-adjustments"><ScrollText className="h-4 w-4 mr-1" />Adjustments</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><OverviewTab statementMonth={statementMonth} onMonthChange={setStatementMonth} /></TabsContent>
        <TabsContent value="cycle" className="mt-4"><CycleTab /></TabsContent>
        <TabsContent value="records" className="mt-4"><RecordsTab statementMonth={statementMonth} /></TabsContent>
        <TabsContent value="reviews" className="mt-4"><ReviewsTab /></TabsContent>
        <TabsContent value="import" className="mt-4"><ImportTab /></TabsContent>
        <TabsContent value="statements" className="mt-4"><StatementsTab /></TabsContent>
        <TabsContent value="payments" className="mt-4"><PaymentsTab /></TabsContent>
        <TabsContent value="rules" className="mt-4"><RulesTab /></TabsContent>
        <TabsContent value="adjustments" className="mt-4"><AdjustmentsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
